import { useCallback, useEffect, useState } from 'react'
import { api, type GitHubConnection, type GitHubRepository } from '../api'
import { timeAgo } from '../time'
import { Icon } from './Icon'

/**
 * "Where your documentation lives" — the connection screen, §31.
 *
 * The backlog row names three things and they are the whole design: **last
 * sync, latest error, reconnect**. Everything on this card is one of those or
 * the choosing that has to happen once before any of them mean anything.
 *
 * The state a customer spends the longest in is the one that used to say
 * nothing, which is the lesson the Domain card above already learned: a screen
 * that will not say is a screen somebody presses buttons on to find out. So
 * every state has a sentence. Not configured, installed-but-nothing-chosen,
 * connected, and needs-reconnect all read differently, and the last one says
 * what stayed — because the whole promise of this card is that a token going
 * bad does not cost you your setup.
 *
 * Reading is ungated: which repository a site reads is not a secret from the
 * people writing it, and somebody who can see "reconnect needed" can tell an
 * admin. Changing it needs owner or admin, which the server enforces and this
 * only reflects.
 */
export function ConnectedRepository({ mayChange }: { mayChange: boolean }) {
  const [state, setState] = useState<GitHubConnection | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const [repositories, setRepositories] = useState<GitHubRepository[] | null>(null)
  const [branches, setBranches] = useState<string[] | null>(null)
  const [repo, setRepo] = useState('')
  const [branch, setBranch] = useState('')
  const [path, setPath] = useState('')

  const load = useCallback(async () => {
    try {
      const found = await api.githubConnection()
      setState(found)
      setRepo(found.connection?.repo ?? '')
      setBranch(found.connection?.branch ?? '')
      setPath(found.connection?.path ?? '')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => void load(), [load])

  /**
   * One busy key rather than a boolean, so a card with five buttons spins
   * exactly one — the idiom every other panel in Settings uses.
   */
  const act = useCallback(
    async (key: string, work: () => Promise<unknown>, done?: string) => {
      setBusy(key)
      setError(null)
      setNote(null)
      try {
        await work()
        await load()
        if (done) setNote(done)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(null)
      }
    },
    [load],
  )

  const pickRepository = useCallback(
    async (chosen: string) => {
      setRepo(chosen)
      setBranches(null)
      if (!chosen) return
      const found = repositories?.find((one) => one.fullName === chosen)
      // The repository's own default is the right first guess, and it stops the
      // commonest way to get this wrong: choosing a repository and leaving the
      // branch box holding the previous repository's branch name.
      setBranch(found?.defaultBranch ?? '')
      await act('branches', async () => {
        setBranches((await api.githubBranches(chosen)).branches)
      })
    },
    [act, repositories],
  )

  if (!state) {
    return (
      <section className="settings__card" data-github-card>
        <h2>Where your documentation lives</h2>
        <span className="skeleton" />
      </section>
    )
  }

  /**
   * A deployment with no GitHub App says so, and offers nothing.
   *
   * The same rule the sign-in doors follow: a door that is present and broken
   * is worse than one that is not there. The card still appears, because the
   * question "can I use my own repository" deserves an answer either way.
   */
  if (!state.configured) {
    return (
      <section className="settings__card" data-github-card>
        <h2>Where your documentation lives</h2>
        <p className="home__quiet">
          This installation of Staleless cannot connect to a repository of your own. Your pages are
          kept in a repository we manage for you, which is shown under Storage below.
        </p>
      </section>
    )
  }

  const connection = state.connection
  const needsReconnect = connection?.state === 'needs_reconnect'

  return (
    <section className="settings__card" data-github-card>
      <h2>Where your documentation lives</h2>
      <p className="home__quiet">
        Connect a repository you own and this site reads and writes there instead. Pull requests
        that change the documentation get a preview and a check on GitHub.
      </p>

      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}
      {note && (
        <p className="screen__note">
          <Icon name="check" size={13} /> {note}
        </p>
      )}

      {/* Nothing installed yet. One button, and it leaves the product. */}
      {!connection && (
        <>
          <p className="home__quiet">
            Your pages are in the repository we manage for you. Installing the app on GitHub is
            where you choose which repositories it may see; you pick which one this site uses
            afterwards, here.
          </p>
          {mayChange && state.installUrl && (
            <div className="settings__row">
              <a className="btn btn--primary" href={state.installUrl} data-github-install>
                <Icon name="link" size={13} /> Connect a GitHub repository
              </a>
            </div>
          )}
          {!mayChange && <p className="home__quiet">An owner or an admin can connect one.</p>}
        </>
      )}

      {connection && (
        <>
          {/*
            The state, said plainly and first. `needs_reconnect` is the one this
            card exists for, so it says what SURVIVED as well as what broke —
            the repository, the branch and the directory are all still here, and
            reconnecting is one press rather than choosing them again.
          */}
          {needsReconnect ? (
            <p className="screen__warn screen__warn--problem">
              <Icon name="alert" size={13} /> GitHub is refusing our access to{' '}
              <code>{connection.repo ?? connection.account}</code>. Your repository, branch and
              folder are all still set — reconnecting puts them back to work.
            </p>
          ) : (
            <p className="screen__note">
              <Icon name="check" size={13} /> Connected to <code>{connection.account}</code>
              {connection.repo ? (
                <>
                  , reading <code>{connection.repo}</code>
                </>
              ) : (
                ' — choose a repository below'
              )}
              .
            </p>
          )}

          <dl className="settings__facts">
            <dt>Repository</dt>
            <dd>{connection.repo ? <code>{connection.repo}</code> : 'Not chosen yet'}</dd>
            <dt>Branch</dt>
            <dd>{connection.branch ? <code>{connection.branch}</code> : 'Not chosen yet'}</dd>
            <dt>Folder</dt>
            <dd>{connection.path ? <code>{connection.path}</code> : 'The whole repository'}</dd>
            <dt>Last sync</dt>
            <dd>
              {connection.lastSyncAt ? (
                <>
                  {timeAgo(connection.lastSyncAt)}
                  {connection.lastSyncSha && (
                    <>
                      {' '}
                      at <code>{connection.lastSyncSha.slice(0, 7)}</code>
                    </>
                  )}
                </>
              ) : (
                'Nothing has come from GitHub yet'
              )}
            </dd>
          </dl>

          {/*
            The latest error, kept until something works. One, not a log: a card
            listing every failure since March is a card nobody reads, and the
            activity log is where history belongs.
          */}
          {connection.lastError && (
            <div className="failure" data-github-error>
              <p className="failure__why">
                <Icon name="alert" size={14} /> {connection.lastError}
              </p>
              {connection.lastErrorAt && (
                <p className="home__quiet">Last failed {timeAgo(connection.lastErrorAt)}.</p>
              )}
            </div>
          )}

          {!state.previewChecks && (
            <p className="screen__warn">
              <Icon name="info" size={13} /> Pull request previews are off on this installation,
              because it has no public address to serve a preview from. Everything else works.
            </p>
          )}

          {mayChange && (
            <>
              <div className="settings__row">
                <button
                  className="btn"
                  disabled={busy !== null}
                  data-github-check
                  onClick={() =>
                    void act('check', () => api.checkGitHubConnection(), 'Asked GitHub.')
                  }
                >
                  {busy === 'check' ? (
                    <Icon name="spinner" size={13} />
                  ) : (
                    <Icon name="check" size={13} />
                  )}{' '}
                  {needsReconnect ? 'Reconnect' : 'Check the connection'}
                </button>
                {state.installUrl && (
                  <a className="btn btn--ghost" href={state.installUrl}>
                    {needsReconnect
                      ? 'Reinstall on GitHub'
                      : 'Change which repositories it can see'}
                  </a>
                )}
              </div>

              {/*
                The picker. Loaded on demand rather than on every render of the
                Settings screen: it is two calls to GitHub, and most visits to
                this screen are about something else entirely.
              */}
              <div className="settings__row">
                <button
                  className="btn btn--tinted"
                  disabled={busy !== null}
                  data-github-choose
                  onClick={() =>
                    void act('repos', async () => {
                      setRepositories((await api.githubRepositories()).repositories)
                    })
                  }
                >
                  {busy === 'repos' ? <Icon name="spinner" size={13} /> : null}
                  {connection.repo ? 'Choose a different repository' : 'Choose a repository'}
                </button>
              </div>

              {repositories && (
                <>
                  <div className="field">
                    <label className="label" htmlFor="gh-repo">
                      Repository
                    </label>
                    <select
                      id="gh-repo"
                      className="select"
                      value={repo}
                      onChange={(e) => void pickRepository(e.target.value)}
                    >
                      <option value="">Choose one…</option>
                      {repositories.map((one) => (
                        <option key={one.fullName} value={one.fullName}>
                          {one.fullName}
                          {one.private ? ' (private)' : ''}
                        </option>
                      ))}
                    </select>
                    {repositories.length === 0 && (
                      <p className="field__note">
                        The app can see no repositories yet. Add one on GitHub, then choose it here.
                      </p>
                    )}
                  </div>

                  <div className="field">
                    <label className="label" htmlFor="gh-branch">
                      Branch
                    </label>
                    <select
                      id="gh-branch"
                      className="select"
                      value={branch}
                      disabled={!branches}
                      onChange={(e) => setBranch(e.target.value)}
                    >
                      <option value="">
                        {branches ? 'Choose one…' : 'Choose a repository first'}
                      </option>
                      {(branches ?? []).map((one) => (
                        <option key={one} value={one}>
                          {one}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="field">
                    <label className="label" htmlFor="gh-path">
                      Folder
                    </label>
                    <input
                      id="gh-path"
                      className="input"
                      value={path}
                      placeholder="docs"
                      onChange={(e) => setPath(e.target.value)}
                    />
                    <p className="field__note">
                      The folder inside the repository that holds <code>nav.yaml</code> and{' '}
                      <code>pages/</code>. Leave it empty if they are at the top.
                    </p>
                  </div>

                  <div className="settings__row">
                    <button
                      className="btn btn--primary"
                      disabled={busy !== null || !repo || !branch}
                      data-github-save
                      onClick={() =>
                        void act(
                          'save',
                          () => api.connectRepository(repo, branch, path),
                          'This site now reads your repository.',
                        )
                      }
                    >
                      {busy === 'save' ? <Icon name="spinner" size={13} /> : null} Use this
                      repository
                    </button>
                    <button
                      className="btn btn--ghost"
                      disabled={busy !== null}
                      onClick={() => {
                        setRepositories(null)
                        setBranches(null)
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}

              <Disconnect
                busy={busy}
                repo={connection.repo}
                onDisconnect={() =>
                  void act(
                    'disconnect',
                    () => api.disconnectRepository(),
                    'Disconnected. This site reads the repository we manage for you again.',
                  )
                }
              />
            </>
          )}
        </>
      )}
    </section>
  )
}

/**
 * Disconnecting, confirmed in place.
 *
 * Inline rather than `window.confirm`, which this codebase forbids and has a
 * test to prove it. Worth confirming at all because it moves where every page
 * in the site is read from, and the sentence says where they go — somebody who
 * presses this should not have to guess whether their pages are about to
 * vanish.
 */
function Disconnect({
  busy,
  repo,
  onDisconnect,
}: {
  busy: string | null
  repo: string | null
  onDisconnect: () => void
}) {
  const [asking, setAsking] = useState(false)
  if (!asking) {
    return (
      <div className="settings__row">
        <button
          className="btn btn--ghost btn--sm"
          disabled={busy !== null}
          data-github-disconnect
          onClick={() => setAsking(true)}
        >
          Disconnect
        </button>
      </div>
    )
  }
  return (
    <div className="pubs__confirm">
      <p className="home__quiet">
        This site will stop reading {repo ? <code>{repo}</code> : 'your repository'} and go back to
        the one we manage for you. Nothing is deleted on GitHub, and the app stays installed until
        you remove it there.
      </p>
      <div className="pubs__actions">
        <button className="btn btn--danger btn--sm" disabled={busy !== null} onClick={onDisconnect}>
          {busy === 'disconnect' ? <Icon name="spinner" size={13} /> : null} Disconnect
        </button>
        <button className="btn btn--ghost btn--sm" onClick={() => setAsking(false)}>
          Keep it connected
        </button>
      </div>
    </div>
  )
}
